// src/pages/OCIStorageDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  DataUsage as DataUsageIcon,
  Public as PublicIcon,
  ShowChart as ShowChartIcon,
  AttachMoney as MoneyIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Import custom hooks and components
import { useAppData } from '../contexts/AppDataContext';
import PageHeader from '../components/common/PageHeader';
import ChartCard from '../components/common/ChartCard';
import LoadingOverlay from '../components/common/LoadingOverlay';

export default function OCIStorageDashboard() {
  const theme = useTheme();
  
  // State
  const [region, setRegion] = useState('');
  const [compartment, setCompartment] = useState('');
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
  const [endDate, setEndDate] = useState(new Date());
  const [storageDetails, setStorageDetails] = useState(null);
  const [storageTrends, setStorageTrends] = useState(null);
  const [storageCosts, setStorageCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Colors for charts
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.tertiary?.main || '#ffb74d',
    theme.palette.quaternary?.main || '#ff9966',
    theme.palette.info.main,
    theme.palette.success.main,
  ];
  
  // Get API functions from context
  const { fetchStorageDetails, fetchStorageTrends, fetchStorageCosts } = useAppData();
  
  // Load data on component mount
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Data fetching function
  const fetchData = async () => {
    setLoading(true);
    try {
      const details = await fetchStorageDetails({ region, compartment });
      const trends = await fetchStorageTrends({ 
        region, 
        compartment, 
        startDate: startDate.toISOString().split('T')[0], 
        endDate: endDate.toISOString().split('T')[0] 
      });
      const costs = await fetchStorageCosts({ 
        region, 
        compartment, 
        startDate: startDate.toISOString().split('T')[0], 
        endDate: endDate.toISOString().split('T')[0] 
      });
      
      setStorageDetails(details);
      setStorageTrends(trends);
      setStorageCosts(costs);
    } catch (error) {
      console.error('Error fetching storage data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };
  
  // Get region options
  const regionOptions = [
    { value: '', label: 'All Regions' },
    { value: 'ap-mumbai-1', label: 'ap-mumbai-1' },
    { value: 'me-abudhabi-1', label: 'me-abudhabi-1' },
  ];
  
  // Get compartment options
  const compartmentOptions = [
    { value: '', label: 'All Compartments' },
    { value: 'eventussecurity-bom', label: 'eventussecurity-bom' },
    { value: 'eventussecurity-AUH', label: 'eventussecurity-AUH' },
  ];
  
  // Calculate totals and prepare chart data
  const bootVolumeTotal = useMemo(() => storageDetails?.boot_volumes?.total_size || 0, [storageDetails]);
  const blockVolumeTotal = useMemo(() => storageDetails?.block_volumes?.total_size || 0, [storageDetails]);
  const totalStorage = useMemo(() => bootVolumeTotal + blockVolumeTotal, [bootVolumeTotal, blockVolumeTotal]);
  
  // Pie chart data
  const pieData = useMemo(() => [
    { name: 'Boot Volumes', value: bootVolumeTotal },
    { name: 'Block Volumes', value: blockVolumeTotal }
  ], [bootVolumeTotal, blockVolumeTotal]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  };

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title="OCI Storage Dashboard"
        subtitle="Monitor and analyze cloud storage resources"
        icon={StorageIcon}
      />

      {/* Filter Card */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: '12px',
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 8px 16px ${alpha('#000', 0.1)}`,
      }}>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Region</InputLabel>
                  <Select
                    value={region}
                    label="Region"
                    onChange={(e) => setRegion(e.target.value)}
                    sx={{
                      borderRadius: '10px',
                      backgroundColor: `${theme.palette.background.paper}80`,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${theme.palette.text.primary}1a`,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${theme.palette.primary.main}4d`,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {regionOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Compartment</InputLabel>
                  <Select
                    value={compartment}
                    label="Compartment"
                    onChange={(e) => setCompartment(e.target.value)}
                    sx={{
                      borderRadius: '10px',
                      backgroundColor: `${theme.palette.background.paper}80`,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${theme.palette.text.primary}1a`,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${theme.palette.primary.main}4d`,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {compartmentOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={2.4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newDate) => setStartDate(newDate)}
                    slotProps={{ 
                      textField: { 
                        size: 'small',
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backgroundColor: `${theme.palette.background.paper}80`,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: `${theme.palette.text.primary}1a`,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: `${theme.palette.primary.main}4d`,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }
                      } 
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} md={2.4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    minDate={startDate}
                    onChange={(newDate) => setEndDate(newDate)}
                    slotProps={{ 
                      textField: { 
                        size: 'small',
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backgroundColor: `${theme.palette.background.paper}80`,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: `${theme.palette.text.primary}1a`,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: `${theme.palette.primary.main}4d`,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }
                      } 
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} md={2.4}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  fullWidth
                  startIcon={<RefreshIcon />}
                  sx={{ 
                    borderRadius: '10px',
                    height: '40px',
                    textTransform: 'none',
                    fontWeight: 'medium',
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    },
                  }}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Main Content */}
      <LoadingOverlay
        loading={loading}
        message="Loading storage data..."
      >
        {/* Summary Row */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: '12px',
              backgroundColor: theme.palette.background.paper,
              boxShadow: `0 8px 16px ${alpha('#000', 0.1)}`,
            }}>
              <Box 
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  alignItems: 'center',
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <DataUsageIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" fontWeight="medium">
                  Storage Summary
                </Typography>
              </Box>
              <CardContent>
                <Box mb={4}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Total Storage
                  </Typography>
                  <Typography variant="h3" color="primary" fontWeight="bold">
                    {totalStorage.toFixed(2)} GB
                  </Typography>
                </Box>
                
                <Box mb={3}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Boot Volumes</Typography>
                    <Typography variant="body2" fontWeight="medium">{bootVolumeTotal.toFixed(2)} GB</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(bootVolumeTotal / (totalStorage || 1)) * 100}
                    sx={{ 
                      height: 8, 
                      borderRadius: 1, 
                      mb: 2,
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: theme.palette.info.main,
                      }
                    }}
                  />
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Block Volumes</Typography>
                    <Typography variant="body2" fontWeight="medium">{blockVolumeTotal.toFixed(2)} GB</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(blockVolumeTotal / (totalStorage || 1)) * 100}
                    sx={{ 
                      height: 8, 
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: theme.palette.success.main,
                      }
                    }}
                  />
                </Box>
                
                <Divider sx={{ my: 2, opacity: 0.1 }} />
                
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Storage Costs
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Boot Volumes</Typography>
                    <Typography variant="body2" color={theme.palette.info.main} fontWeight="medium">
                      {formatCurrency(storageDetails?.boot_volumes?.total_cost || 0)}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Block Volumes</Typography>
                    <Typography variant="body2" color={theme.palette.success.main} fontWeight="medium">
                      {formatCurrency(storageDetails?.block_volumes?.total_cost || 0)}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2, opacity: 0.1 }} />
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle1" fontWeight="medium">Total Cost</Typography>
                    <Typography variant="subtitle1" color={theme.palette.primary.main} fontWeight="bold">
                      {formatCurrency((storageDetails?.boot_volumes?.total_cost || 0) + (storageDetails?.block_volumes?.total_cost || 0))}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <ChartCard
              title="Storage Distribution"
              icon={DataUsageIcon}
              height={350}
              chart={
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                      labelLine={true}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => `${value.toFixed(2)} GB`} 
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        color: theme.palette.text.primary
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ 
                        paddingTop: 20,
                        color: theme.palette.text.secondary 
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
              }
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <ChartCard
              title="Storage by Region"
              icon={PublicIcon}
              height={350}
              chart={storageDetails?.by_region && storageDetails.by_region.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={storageDetails.by_region}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={`${theme.palette.text.primary}1a`} />
                    <XAxis 
                      dataKey="region" 
                      tick={{ fill: theme.palette.text.secondary }}
                    />
                    <YAxis 
                      unit=" GB" 
                      tick={{ fill: theme.palette.text.secondary }}
                    />
                    <Tooltip 
                      formatter={(value) => `${value.toFixed(2)} GB`} 
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        color: theme.palette.text.primary
                      }}
                    />
                    <Legend wrapperStyle={{ color: theme.palette.text.secondary }} />
                    <Bar dataKey="boot_volume_size" name="Boot Volumes" fill={theme.palette.info.main} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="block_volume_size" name="Block Volumes" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            />
          </Grid>
        </Grid>

        {/* Trends Row */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={8}>
            <ChartCard
              title="Storage Growth Trend"
              subtitle="Changes in storage allocation over time"
              icon={ShowChartIcon}
              height={300}
              chart={storageTrends?.data && storageTrends.data.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={storageTrends.data}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={`${theme.palette.text.primary}1a`} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: theme.palette.text.secondary }}
                    />
                    <YAxis 
                      unit=" GB" 
                      tick={{ fill: theme.palette.text.secondary }}
                    />
                    <Tooltip 
                      formatter={(value) => `${value.toFixed(2)} GB`} 
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        color: theme.palette.text.primary
                      }}
                    />
                    <Legend wrapperStyle={{ color: theme.palette.text.secondary }} />
                    <Line
                      type="monotone"
                      dataKey="boot_volume_size"
                      name="Boot Volumes"
                      stroke={theme.palette.info.main}
                      activeDot={{ r: 8 }}
                      dot={{ r: 1 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="block_volume_size"
                      name="Block Volumes"
                      stroke={theme.palette.success.main}
                      activeDot={{ r: 8 }}
                      dot={{ r: 1 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="total_size"
                      name="Total Storage"
                      stroke={theme.palette.primary.main}
                      activeDot={{ r: 8 }}
                      dot={{ r: 1 }}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <ChartCard
              title="Storage Cost Trend"
              subtitle="Cost changes over the selected period"
              icon={MoneyIcon}
              height={300}
              chart={storageCosts?.data && storageCosts.data.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={storageCosts.data}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={`${theme.palette.text.primary}1a`} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: theme.palette.text.secondary }}
                    />
                    <YAxis 
                      unit=" ₹" 
                      tick={{ fill: theme.palette.text.secondary }}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)} 
                      contentStyle={{ 
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        color: theme.palette.text.primary
                      }}
                    />
                    <Legend wrapperStyle={{ color: theme.palette.text.secondary }} />
                    <Line
                      type="monotone"
                      dataKey="boot_volume_cost"
                      name="Boot Volumes"
                      stroke={theme.palette.info.main}
                      activeDot={{ r: 8 }}
                      dot={{ r: 1 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="block_volume_cost"
                      name="Block Volumes"
                      stroke={theme.palette.success.main}
                      activeDot={{ r: 8 }}
                      dot={{ r: 1 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="total_cost"
                      name="Total Cost"
                      stroke={theme.palette.primary.main}
                      activeDot={{ r: 8 }}
                      dot={{ r: 1 }}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            />
          </Grid>
        </Grid>

        {/* Table */}
        <Card sx={{ 
          borderRadius: '12px',
          backgroundColor: theme.palette.background.paper,
          boxShadow: `0 8px 16px ${alpha('#000', 0.1)}`,
        }}>
          <Box 
            sx={{ 
              p: 2, 
              display: 'flex', 
              alignItems: 'center',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <BarChartIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
            <Typography variant="h6" fontWeight="medium">
              Top Storage Consumers
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{
                  '& th': {
                    backgroundColor: `${theme.palette.background.paper}cc`,
                    backdropFilter: 'blur(8px)',
                    color: theme.palette.text.secondary,
                    fontWeight: 'medium',
                    borderBottom: `1px solid ${theme.palette.divider}1a`,
                  }
                }}>
                  <TableCell>Instance Name</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell>Compartment</TableCell>
                  <TableCell>Boot Volume (GB)</TableCell>
                  <TableCell>Block Volumes (GB)</TableCell>
                  <TableCell>Total Storage (GB)</TableCell>
                  <TableCell>Monthly Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {storageDetails?.top_consumers?.map((instance, index) => (
                  <TableRow 
                    key={index}
                    hover
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: `${theme.palette.primary.main}0a`,
                      },
                      '& td': {
                        borderBottom: `1px solid ${theme.palette.divider}0d`,
                      }
                    }}
                  >
                    <TableCell>{instance.instance_name}</TableCell>
                    <TableCell>{instance.region_name}</TableCell>
                    <TableCell>{instance.compartment_name}</TableCell>
                    <TableCell>{instance.boot_volume_size}</TableCell>
                    <TableCell>{instance.block_volume_total_size}</TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      {instance.boot_volume_size + instance.block_volume_total_size}
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.success.main, fontWeight: 'medium' }}>
                      {formatCurrency(instance.storage_cost)}
                    </TableCell>
                  </TableRow>
                ))}
                {(!storageDetails?.top_consumers || storageDetails.top_consumers.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No data available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </LoadingOverlay>
    </Box>
  );
}